import { toast } from "react-toastify";

import type {
  CancelAgenticLogicRuleChecksRequestBodyType,
  CancelAgenticLogicRuleChecksResponseBodyType,
} from "@intelligems/ig-types/functions/agentic-logic/publish/cancel-checks";
import type { GetAgenticLogicRulePublishJobResponseBodyType } from "@intelligems/ig-types/functions/agentic-logic/publish/get-publish-job";
import { getMutationErrorMessage } from "@src/queryClient";
import { hashKey, useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

const cancelAgenticLogicRuleChecks = async (
  payload: ApiRequest<
    CancelAgenticLogicRuleChecksRequestBodyType,
    { ruleId: string }
  >
) => {
  const resp = await api.post<
    CancelAgenticLogicRuleChecksResponseBodyType,
    CancelAgenticLogicRuleChecksRequestBodyType
  >(
    `/v2/agentic-logic/rules/${payload.pathParams.ruleId}/cancel-checks`,
    payload.body
  );
  return resp.data;
};

/**
 * "Cancel" on the running checks strip. Stops the rule's check operation: the
 * named publish job, any other job checking the rule beside it, and the live
 * repair loop. Idempotent on the server: a run that finished first answers its
 * final state.
 *
 * The response IS the named job, so it is seeded into the job poll's cache —
 * the strip leaves its running state on the reply rather than a poll later,
 * and the poll stops because the status is terminal. Every other job of the
 * rule is then refetched, since the server may have stopped the one the strip
 * follows next; the rule for its repair run and latest job; the rules list for
 * the deploy chip.
 */
export const useCancelAgenticLogicRuleChecks = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CancelAgenticLogicRuleChecksResponseBodyType,
    Error,
    ApiRequest<CancelAgenticLogicRuleChecksRequestBodyType, { ruleId: string }>,
    unknown
  >({
    mutationFn: async (data) => cancelAgenticLogicRuleChecks(data),
    onSuccess: async (result, variables) => {
      const { ruleId } = variables.pathParams;
      const seededJobKey = agenticLogicKeys.publishJob(ruleId, result.job.id);
      const seededJobHash = hashKey(seededJobKey);
      queryClient.setQueryData<GetAgenticLogicRulePublishJobResponseBodyType>(
        seededJobKey,
        { job: result.job }
      );
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: agenticLogicKeys.publishJobs(ruleId),
          predicate: (query) => query.queryHash !== seededJobHash,
        }),
        queryClient.invalidateQueries({
          queryKey: agenticLogicKeys.rule(ruleId),
        }),
        queryClient.invalidateQueries({ queryKey: agenticLogicKeys.rules() }),
      ]);
    },
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to cancel the checks${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};
